// EditEspacioModal.tsx — Co Marca — Editar Espacio
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit, Store, Users, MapPin, Info, CheckCircle2 } from 'lucide-react';
import { updateEspacio, Espacio } from '../../services/espacioService';
import { fetchSucursales, Sucursal } from '../../services/pos/sucursalService';
import ImageUpload from '../Upload/ImageUpload';

interface EditEspacioModalProps {
    isOpen: boolean; onClose: () => void; onEspacioUpdated: () => void; espacio: Espacio|null;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.eem-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes eemFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes eemSlideUp{from{opacity:0;transform:translateY(24px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.eem-overlay{animation:eemFadeIn .22s ease both;}
.eem-dialog{animation:eemSlideUp .32s cubic-bezier(.16,1,.3,1) both;}
.eem-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fafafa;font-size:.88rem;color:#374151;outline:none;transition:all .2s;}
.eem-input:focus{border-color:${colors.secondary};box-shadow:0 0 0 3px ${colors.secondary}12;background:#fff;}
.eem-textarea{resize:none;min-height:100px;}
.eem-select{appearance:none;}
.eem-btn-primary{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.secondary},#004d7a);color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(97, 37, 123,.28);transition:all .22s cubic-bezier(.16,1,.3,1);}
.eem-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(97, 37, 123,.34);}
.eem-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.eem-btn-ghost{display:inline-flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;color:#374151;border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;}
.eem-btn-ghost:hover{background:#f1f5f9;}
.custom-scroll::-webkit-scrollbar{width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background:rgba(97, 37, 123,.5);}
`;

const EditEspacioModal: React.FC<EditEspacioModalProps> = ({ isOpen, onClose, onEspacioUpdated, espacio }) => {
    const [nombre, setNombre]               = useState('');
    const [descripcion, setDescripcion]     = useState('');
    const [cantidadPersonas, setCantidad]   = useState('');
    const [sucursal, setSucursal]           = useState('');
    const [sucursales, setSucursales]       = useState<Sucursal[]>([]);
    const [imagen, setImagen]               = useState<File|null>(null);
    const [estado, setEstado]               = useState(true);
    const [isLoading, setIsLoading]         = useState(false);
    const [error, setError]                 = useState<string|null>(null);

    useEffect(() => {
        if (isOpen) {
            fetchSucursales().then(d=>setSucursales(d.filter(s=>s.activo))).catch(()=>setError('Error al cargar sucursales.'));
            if (espacio) {
                setNombre(espacio.nombre); setDescripcion(espacio.descripcion||'');
                setCantidad(espacio.cantidad_personas.toString()); setSucursal(espacio.sucursal.toString());
                setEstado(espacio.estado); setImagen(null); setError(null);
            }
        }
    }, [isOpen, espacio]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!espacio) return;
        if (!sucursal) { setError('Debe seleccionar una sucursal'); return; }
        if (!cantidadPersonas || parseInt(cantidadPersonas)<1) { setError('La capacidad debe ser al menos 1'); return; }
        setIsLoading(true); setError(null);
        try {
            await updateEspacio(espacio.id, { nombre, descripcion, cantidad_personas:parseInt(cantidadPersonas), imagen, estado, sucursal:parseInt(sucursal) });
            onEspacioUpdated(); onClose();
        } catch (err: any) { setError(err.response?.data?.message||'Error al actualizar el espacio.'); }
        finally { setIsLoading(false); }
    };

    if (!isOpen || !espacio) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="eem-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="eem-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>
                <div className="eem-dialog custom-scroll" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'640px', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid #f1f5f9' }}>

                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.secondary},${colors.secondaryLight},${colors.accentDark},${colors.primary})`, borderRadius:'24px 24px 0 0' }}/>

                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${colors.secondary}30` }}>
                                    <Edit size={17} color="#fff"/>
                                </div>
                                <div>
                                    <h3 style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Editar Espacio</h3>
                                    <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Modificando <strong style={{ color:'#374151' }}>{espacio.nombre}</strong></p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.secondary,colors.secondaryLight,colors.accentDark,colors.primary].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={15} color="#64748b"/>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}>
                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}22`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:colors.primaryDark, fontWeight:'600' }}>{error}</span>
                            </div>
                        )}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <MapPin size={11} color={colors.secondary}/> Sucursal *
                                </label>
                                <select required value={sucursal} onChange={e=>setSucursal(e.target.value)} className="eem-input eem-select">
                                    <option value="">Seleccione una sucursal</option>
                                    {sucursales.map(s=><option key={s.id} value={s.id}>{s.nombre} ({s.codigo})</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Store size={11} color={colors.secondary}/> Nombre *
                                </label>
                                <input type="text" required value={nombre} onChange={e=>setNombre(e.target.value)} placeholder="Ej: Sala de Conferencias" className="eem-input"/>
                            </div>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Descripción</label>
                                <textarea value={descripcion} onChange={e=>setDescripcion(e.target.value)} placeholder="Características, equipamiento, etc." className="eem-input eem-textarea"/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Imagen</label>
                                <div style={{ height:'100px' }}>
                                    <ImageUpload currentImage={espacio.imagen||undefined} setImagen={setImagen}/>
                                </div>
                            </div>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Users size={11} color={colors.accentDark}/> Capacidad *
                                </label>
                                <input type="number" required min="1" value={cantidadPersonas} onChange={e=>setCantidad(e.target.value)} placeholder="Ej: 10" className="eem-input"/>
                            </div>
                            <div style={{ background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                <div>
                                    <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a', marginBottom:'2px' }}>Estado del espacio</p>
                                    <p style={{ fontSize:'.7rem', color:estado?colors.secondaryLight:'#94a3b8', fontWeight:'600' }}>
                                        {estado?'● Disponible para reservas':'○ No disponible'}
                                    </p>
                                </div>
                                <div onClick={()=>setEstado(!estado)} style={{ width:'44px', height:'24px', borderRadius:'100px', background:estado?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                    <div style={{ position:'absolute', top:'3px', left:estado?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                                </div>
                            </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', background:`${colors.secondary}08`, border:`1.5px solid ${colors.secondary}18`, borderRadius:'10px', padding:'10px 14px' }}>
                            <Info size={14} color={colors.secondary} style={{ flexShrink:0, marginTop:'1px' }}/>
                            <p style={{ fontSize:'.75rem', color:'#374151', lineHeight:1.55 }}>
                                La imagen actual se mantendrá si no seleccionas una nueva.
                            </p>
                        </div>
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="eem-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={isLoading} className="eem-btn-primary">
                                {isLoading ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</> : <><CheckCircle2 size={14}/> Actualizar Espacio</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default EditEspacioModal;